import PublicPage, { Section, Bullets } from "@/components/PublicPage";
import { COMPANY } from "@/lib/company";
import { Button } from "@/components/ui/button";

const ROWS = [
  ["access_token / refresh_token", "Essential", "Keeps you signed in securely (httpOnly). Without it you cannot use the workspace.", "12 hours / 7 days"],
  ["cx_token, cx_user, cx_trial", "Essential", "Local storage entries that keep your session and trial state available, including when the site is offline.", "Until sign-out"],
  ["ps_cookie_consent", "Essential", "Remembers the cookie choice you made on this banner.", "12 months"],
  ["plansarthi-offline (IndexedDB)", "Essential", "Holds DPR entries logged with no network signal plus cached project data, so nothing is lost in the field.", "Until synced / cleared"],
  ["theme", "Preference", "Remembers whether you chose the dark or light workspace.", "12 months"],
  ["Product analytics (PostHog)", "Analytics — optional", "Anonymised feature usage and error diagnostics that show us what to improve.", "Up to 12 months"],
  ["Razorpay checkout cookies", "Essential (payment)", "Set by Razorpay during checkout to complete and secure a transaction.", "Session"],
];

export default function CookiePolicy() {
  return (
    <PublicPage
      testId="cookie-policy-page"
      title="Cookie Policy"
      intro="This policy explains the cookies and similar local-storage technologies Plan-Sarthi uses, why we use them, and how you can refuse the optional ones. It should be read with our Privacy Policy."
    >
      <Section n={1} title="What cookies are">
        <p>
          Cookies are small text files a website stores on your device. We also use two similar browser technologies:
          local storage (for your session and preferences) and IndexedDB (for the offline DPR queue). For simplicity
          we refer to all three as "cookies" in this policy.
        </p>
      </Section>

      <Section n={2} title="The cookies we use">
        <div className="overflow-x-auto border border-border">
          <table className="w-full dense-table" data-testid="cookie-table">
            <thead className="sticky-head">
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Purpose</th>
                <th>Retention</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([name, cat, purpose, life]) => (
                <tr key={name}>
                  <td className="num text-[11.5px]">{name}</td>
                  <td className={cat.includes("optional") ? "text-[#F97316]" : ""}>{cat}</td>
                  <td className="text-muted-foreground">{purpose}</td>
                  <td className="text-muted-foreground whitespace-nowrap">{life}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section n={3} title="Why we use them">
        <Bullets
          items={[
            "Essential: to authenticate you, protect against session hijacking and brute-force attempts, complete payments, and keep field data safe when connectivity drops. These cannot be switched off without breaking the platform.",
            "Preference: to remember your theme so the workspace looks the way you left it.",
            "Analytics (optional): to understand which modules are used, where users get stuck and which errors occur, so we can prioritise fixes. We do not use analytics cookies for advertising, and we do not sell this data.",
          ]}
        />
        <p>We do not use advertising, retargeting or cross-site tracking cookies of any kind.</p>
      </Section>

      <Section n={4} title="Your choices and how to opt out">
        <p>
          On your first visit a consent banner appears with <strong>Accept all</strong> and{" "}
          <strong>Reject optional</strong>. Analytics stay switched off until you accept. If you press "Reject
          optional", analytics capture and session recording are disabled immediately and no analytics cookies are
          set — the rest of the platform continues to work normally.
        </p>
        <p>You can change your mind at any time:</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { localStorage.removeItem("ps_cookie_consent"); window.location.reload(); }}
          data-testid="cookie-reset-btn"
        >
          Reset my cookie choice
        </Button>
        <p>
          You can also block or delete cookies in your browser settings (Chrome, Edge, Firefox and Safari all provide
          per-site controls). Blocking essential cookies will sign you out and prevent the workspace from loading.
        </p>
      </Section>

      <Section n={5} title="Third-party cookies">
        <p>
          Razorpay sets its own cookies inside the checkout window to process payments securely, and Google may set
          cookies during the optional Google sign-in flow. These are governed by their respective privacy and cookie
          policies. Plan-Sarthi cannot read them.
        </p>
      </Section>

      <Section n={6} title="Updates and contact">
        <p>
          We will update this policy whenever the cookies we use change, and revise the effective date above. Questions
          about cookies or this policy: <strong>{COMPANY.email}</strong>, {COMPANY.legalName}, {COMPANY.address}.
        </p>
      </Section>
    </PublicPage>
  );
}
